import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Landmark, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { downloadFile } from "../../utils/download";
import UserDetailsModal, { LoyaltyCustomer } from "../../components/UserDetailsModal";

export interface AdminBalanceData {
  balance: number;
  customersHeld: {
    total: number;
    wallet: number;
    walletCustomers: number;
    giftCards: number;
    giftCardCount: number;
  };
  restaurants: { net: number; owedToPlatform: number; owedByPlatform: number };
  issuedByAdmin: {
    total: number;
    compensation: number;
    compensationCount: number;
    manualPoints: number;
    manualPointsCount: number;
    birthdayRewards?: number;
    birthdayRewardsCount?: number;
    birthdayExpired?: number;
    reversed: number;
  };
  loggedTotal: number;
  unlogged: number;
}

type Category = "all" | "wallet" | "bonus" | "giftcard" | "restaurant" | "admin";

interface AdminLogEntry {
  _id: string;
  createdAt: string;
  category: Exclude<Category, "all">;
  activity: string;
  description: string;
  /** id is missing for gift cards bought without an account */
  customer: { id?: string; name: string; phone: string } | null;
  restaurant: string;
  direction: "in" | "out";
  amount: number;
  effect: number;
  /** The bonus wallet credit this entry funds is folded into this row */
  bonusCredited?: boolean;
}

const CATEGORY_STYLE: Record<AdminLogEntry["category"], { label: string; className: string }> = {
  wallet: { label: "Customer wallet", className: "bg-teal-50 text-teal-700" },
  bonus: { label: "Bonus points", className: "bg-purple-50 text-purple-700" },
  giftcard: { label: "Gift card", className: "bg-amber-50 text-amber-700" },
  restaurant: { label: "Restaurant", className: "bg-blue-50 text-blue-700" },
  admin: { label: "Issued by admin", className: "bg-red-50 text-red-700" },
};

const PAGE_SIZE = 20;

const fmt = (n: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);

/** "+$1.00" / "−$1.00" */
const signed = (n: number, currency: string) =>
  `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmt(Math.abs(n), currency)}`;

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const toLocalDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** One line of the balance breakdown. */
const Line: React.FC<{ label: string; note?: string; value: number; currency: string }> = ({
  label,
  note,
  value,
  currency,
}) => (
  <div className="flex justify-between gap-4 py-2">
    <div>
      <p className="m-0 text-sm text-gray-800">{label}</p>
      {note && <p className="m-0 text-xs text-gray-500">{note}</p>}
    </div>
    <span
      className={`text-sm font-semibold tabular-nums whitespace-nowrap ${
        value > 0 ? "text-green-700" : value < 0 ? "text-red-600" : "text-gray-500"
      }`}
    >
      {signed(value, currency)}
    </span>
  </div>
);

/** Admin Balance on its own page (/admin/ledger/admin-balance). Master admins only. */
const AdminBalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { currency } = useAuth();
  const [balance, setBalance] = useState<AdminBalanceData | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toLocalDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => toLocalDateStr(new Date()));
  const [category, setCategory] = useState<Category>("all");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewUser, setViewUser] = useState<LoyaltyCustomer | null>(null);
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);
  const [log, setLog] = useState<{ entries: AdminLogEntry[]; total: number; page: number; pages: number } | null>(
    null
  );

  useEffect(() => {
    api
      .get("/ledger/admin-balance")
      .then((res) => res.data.success && setBalance(res.data.data))
      .catch(() => toast.error("Failed to load admin balance"));
  }, [reloadKey]);

  const filterParams = useCallback(() => {
    const params: Record<string, string> = {};
    if (category !== "all") params.category = category;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [category, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/ledger/admin-balance/log", { params: { ...filterParams(), page, limit: PAGE_SIZE } })
      .then((res) => {
        if (!cancelled && res.data.success) setLog(res.data.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load admin log");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, filterParams, reloadKey]);

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadFile(
        "/ledger/admin-balance/export",
        filterParams(),
        `admin-balance-${startDate || "all"}-to-${endDate || "today"}.xlsx`
      );
    } catch {
      toast.error("Failed to export admin log");
    } finally {
      setExporting(false);
    }
  };

  const b = balance;
  const pageEffect = log ? log.entries.reduce((s, e) => s + e.effect, 0) : 0;

  const openUser = async (userId: string) => {
    try {
      setOpeningUserId(userId);
      const res = await api.get(`/loyalty/users/by-id/${userId}`);
      if (res.data.success) setViewUser(res.data.data);
    } catch {
      toast.error("Failed to load customer");
    } finally {
      setOpeningUserId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {viewUser && (
        <UserDetailsModal
          user={viewUser}
          onClose={() => setViewUser(null)}
          onBalanceChange={() => setReloadKey((k) => k + 1)}
        />
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/ledger")}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Back to ledger"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 m-0">Admin Balance</h1>
            <p className="text-sm text-gray-500 mt-1">
              Where the platform stands with customers and restaurants · all locations
            </p>
          </div>
        </div>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {b && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Final figure */}
          <div
            className={`rounded-xl border p-6 shadow-sm ${
              b.balance >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Landmark className={`w-5 h-5 ${b.balance >= 0 ? "text-green-600" : "text-red-600"}`} />
              <p className="text-xs font-medium uppercase tracking-wide text-gray-600 m-0">Admin balance · right now</p>
            </div>
            <p className={`text-4xl font-bold tabular-nums m-0 ${b.balance >= 0 ? "text-green-700" : "text-red-700"}`}>
              {signed(b.balance, currency)}
            </p>
            <p className="text-sm text-gray-600 mt-2 mb-0">
              Customer money held, plus what restaurants owe (or minus what they are owed), minus points the admin
              issued.
            </p>
            {Math.abs(b.unlogged) >= 0.01 && (
              <p className="text-xs text-amber-700 mt-3 mb-0 flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {signed(b.unlogged, currency)} of this comes from balance changes with no log entry (balances set
                directly, expired gift cards or older data). The log adds up to {signed(b.loggedTotal, currency)}.
              </p>
            )}
          </div>

          {/* How it adds up */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0 mb-1">How it adds up</p>
            <div className="divide-y divide-gray-100">
              <Line
                label="Customer money held"
                note={`Unspent wallets ${fmt(b.customersHeld.wallet, currency)} (${b.customersHeld.walletCustomers} customers) + unredeemed gift cards ${fmt(b.customersHeld.giftCards, currency)} (${b.customersHeld.giftCardCount})`}
                value={b.customersHeld.total}
                currency={currency}
              />
              <Line
                label={b.restaurants.net >= 0 ? "Restaurants owe the platform" : "Platform owes restaurants"}
                note={`Owed to platform ${fmt(b.restaurants.owedToPlatform, currency)} · owed by platform ${fmt(b.restaurants.owedByPlatform, currency)} · includes loyalty and bonus points`}
                value={b.restaurants.net}
                currency={currency}
              />
              <Line
                label="Issued by admin"
                note={`Compensation paid by platform ${fmt(b.issuedByAdmin.compensation, currency)} (${b.issuedByAdmin.compensationCount}) + manual points ${fmt(b.issuedByAdmin.manualPoints, currency)} (${b.issuedByAdmin.manualPointsCount})${b.issuedByAdmin.birthdayRewards ? ` + birthday rewards ${fmt(b.issuedByAdmin.birthdayRewards, currency)} (${b.issuedByAdmin.birthdayRewardsCount ?? 0})` : ""}${b.issuedByAdmin.birthdayExpired ? ` − birthday expired ${fmt(b.issuedByAdmin.birthdayExpired, currency)}` : ""}${b.issuedByAdmin.reversed ? ` − taken back ${fmt(b.issuedByAdmin.reversed, currency)}` : ""}`}
                value={-b.issuedByAdmin.total}
                currency={currency}
              />
              <div className="flex justify-between gap-4 pt-3">
                <span className="text-sm font-semibold text-gray-900">Admin balance</span>
                <span
                  className={`text-sm font-bold tabular-nums ${b.balance >= 0 ? "text-green-700" : "text-red-700"}`}
                >
                  {signed(b.balance, currency)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete log */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <h2 className="font-semibold text-gray-800 m-0 mb-3">All admin logs</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Show</label>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value as Category); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Everything</option>
              <option value="wallet">Customer wallets</option>
              <option value="bonus">Bonus points</option>
              <option value="giftcard">Gift cards (Square)</option>
              <option value="restaurant">Restaurants</option>
              <option value="admin">Issued by admin</option>
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Download Excel"}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3 mb-0">
          "Effect" is how each entry moves the admin balance: money added to a customer wallet or gift card is +,
          wallet money spent is −, restaurant entries are + when the restaurant owes and − when it is owed, and points
          the admin issues are −. Bonus points funded by restaurants have no effect (their restaurant entry carries it).
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading && !log ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : !log || log.entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No entries for this period</div>
        ) : (
          <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                  <th className="text-left px-6 py-3 font-medium">Date</th>
                  <th className="text-left px-6 py-3 font-medium">Activity</th>
                  <th className="text-left px-6 py-3 font-medium">Who</th>
                  <th className="text-right px-6 py-3 font-medium">Amount</th>
                  <th className="text-right px-6 py-3 font-medium">Effect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {log.entries.map((e) => (
                  <tr key={`${e.category}-${e._id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_STYLE[e.category].className}`}
                      >
                        {CATEGORY_STYLE[e.category].label}
                      </span>
                      <p className="m-0 mt-1 text-gray-800">{e.activity}</p>
                      {e.description && <p className="m-0 text-xs text-gray-500">{e.description}</p>}
                    </td>
                    <td className="px-6 py-4">
                      {e.restaurant && <p className="m-0 font-medium text-gray-900">{e.restaurant}</p>}
                      {e.customer ? (
                        <p className={`m-0 ${e.restaurant ? "text-xs text-gray-500" : "font-medium text-gray-900"}`}>
                          {e.customer.id ? (
                            <button
                              type="button"
                              onClick={() => openUser(e.customer!.id!)}
                              disabled={openingUserId === e.customer.id}
                              className="text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 text-left"
                            >
                              {[e.customer.name, e.customer.phone].filter(Boolean).join(" · ")}
                            </button>
                          ) : (
                            [e.customer.name, e.customer.phone].filter(Boolean).join(" · ")
                          )}
                        </p>
                      ) : (
                        !e.restaurant && <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <span className="font-medium tabular-nums text-gray-900">{fmt(e.amount, currency)}</span>
                      <p className="m-0 text-xs text-gray-500">
                        {e.category === "restaurant"
                          ? e.direction === "in"
                            ? "restaurant owes"
                            : "owed to restaurant"
                          : e.category === "giftcard"
                            ? "paid by Square"
                            : e.category === "admin"
                              ? e.direction === "in"
                                ? "taken back"
                                : "given to customer"
                            : e.direction === "in"
                              ? "added"
                              : "used"}
                      </p>
                      {e.bonusCredited && (
                        <p className="m-0 text-xs text-purple-700">added to customer's bonus wallet</p>
                      )}
                    </td>
                    <td
                      className={`px-6 py-4 text-right whitespace-nowrap font-semibold tabular-nums ${
                        e.effect > 0 ? "text-green-700" : e.effect < 0 ? "text-red-600" : "text-gray-400"
                      }`}
                    >
                      {e.effect === 0 ? "—" : signed(e.effect, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={4} className="px-6 py-3 text-right text-gray-600">
                    Effect of the entries on this page
                  </td>
                  <td
                    className={`px-6 py-3 text-right font-semibold tabular-nums ${
                      pageEffect > 0 ? "text-green-700" : pageEffect < 0 ? "text-red-600" : "text-gray-500"
                    }`}
                  >
                    {signed(Math.round(pageEffect * 100) / 100, currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {log && log.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Page {log.page} of {log.pages} · {log.total} entries
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={log.page <= 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(log.pages, p + 1))}
                disabled={log.page >= log.pages || loading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBalancePage;
